import React, { useEffect, useState } from 'react';

/**
 * Hook: format the time-to-end for a spotlighted thing. Ticks every
 * 30s so a countdown crossing an hour boundary doesn't sit stale.
 *
 * Returns null when the spotlight has already ended (callers can
 * hide the countdown then) or when `endTime` is undefined.
 */
export function useCountdown(endTime: string | undefined): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endTime) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [endTime]);
  if (!endTime) return null;
  const remaining = new Date(endTime).getTime() - now;
  if (remaining <= 0) return null;
  const minutes = Math.floor(remaining / 60_000);
  if (minutes < 60) return `${minutes}m left`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m left`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h left`;
}

interface SpotlightCountdownProps {
  endTime?: string;
  /** Optional wrapper class so callers can fit the countdown into
   *  whatever layout they have (default is just a span). */
  className?: string;
  /** Set false to drop the ⏳ icon (e.g. when sitting next to a
   *  ⭐ Spotlighted badge that already conveys the spotlight state). */
  showIcon?: boolean;
}

/**
 * Small text countdown for spotlighted things. Renders nothing when
 * `endTime` is missing or already past — the parent can wrap it in
 * a conditional or just let it render `null`.
 *
 * Used on:
 *   - Dashboard "Spotlighted Right Now" carousel (TopProfilesCarousel)
 *   - Marketplace ProfileCard (when spotlighted)
 *   - PostCard (when spotlighted)
 */
export const SpotlightCountdown: React.FC<SpotlightCountdownProps> = ({
  endTime,
  className,
  showIcon = true,
}) => {
  const label = useCountdown(endTime);
  if (!label) return null;
  return (
    <span className={className}>
      {showIcon && <>⏳ </>}
      {label}
    </span>
  );
};

export default SpotlightCountdown;
